import React from 'react';
import { X, Loader2, CheckCircle2, AlertCircle, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { Project, TranslationFile } from '../../types';
import { useJobManager, Job as ManagerJob } from '../../contexts/JobManagerContext';

const toDate = (v: any): Date | undefined => {
  if (!v) return undefined;
  if (v instanceof Date) return isNaN(v.getTime()) ? undefined : v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? undefined : d;
};

type Mode = 'translate' | 'train';

interface JobsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: Mode;
  files: TranslationFile[];
  projects: Project[];
}

type DisplayStatus = 'running' | 'completed' | 'failed';

interface DisplayJob {
  id: string;
  fileName: string;
  projectName: string;
  status: DisplayStatus;
  progress: number;
  indeterminate: boolean;
  batch?: { current: number; total: number };
  startTime?: Date;
  endTime?: Date;
  errorMsg?: string;
  /** Phase label hiện tại (running only): "Ghép cặp" / "Dịch" / "Kiểm định" */
  phaseLabel?: string;
  /** Loại job để hiện chip: "Dịch" / "Kiểm tra" / "Dịch + Kiểm tra" / "Huấn luyện" / etc. */
  typeTag: string;
}

const STATUS_CONFIG: Record<DisplayStatus, { label: string; cls: string; bg: string }> = {
  running: { label: 'Đang chạy', cls: 'text-amber-700', bg: 'bg-amber-100' },
  completed: { label: 'Hoàn thành', cls: 'text-emerald-700', bg: 'bg-emerald-100' },
  failed: { label: 'Lỗi', cls: 'text-red-700', bg: 'bg-red-100' },
};

const TYPE_TAG_CONFIG: Record<string, { cls: string; bg: string }> = {
  'Dịch':             { bg: 'bg-sky-100',     cls: 'text-sky-700' },
  'Kiểm tra':         { bg: 'bg-indigo-100',  cls: 'text-indigo-700' },
  'Dịch + Kiểm tra':  { bg: 'bg-teal-100',    cls: 'text-teal-700' },
  'Đối chiếu':        { bg: 'bg-slate-100',   cls: 'text-slate-600' },
  'Kiểm định':        { bg: 'bg-violet-100',  cls: 'text-violet-700' },
  'Huấn luyện':       { bg: 'bg-purple-100',  cls: 'text-purple-700' },
};

const STEP_PROGRESS_FALLBACK: Record<number, number> = {
  1: 10,
  2: 50,
  2.5: 65,
  3: 80,
  4: 100,
};

const PAGE_SIZE = 10;

const formatRelative = (date?: Date): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s trước`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} phút trước`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} giờ trước`;
  return `${Math.floor(diffHr / 24)} ngày trước`;
};

const formatDuration = (start?: Date, end?: Date): string => {
  if (!start) return '—';
  const startTime = start instanceof Date ? start : new Date(start);
  const endTime = end ? (end instanceof Date ? end : new Date(end)) : new Date();
  const sec = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (min < 60) return `${min}p ${remSec}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}p`;
};

const mapFileToJob = (
  file: TranslationFile,
  projectName: string,
  mode: Mode,
  managerJob: ManagerJob | undefined,
): DisplayJob | null => {
  // Train mode: combine match + translate + audit status thành 1 row, hiện phase đang chạy
  let displayStatus: DisplayStatus;
  let phaseLabel: string | undefined;

  let typeTag: string;

  if (mode === 'train') {
    const m = file.matchStatus;
    const t = file.translationStatus;
    const a = file.auditStatus;
    if (m === 'running') { displayStatus = 'running'; phaseLabel = 'Đối chiếu'; }
    else if (t === 'running') { displayStatus = 'running'; phaseLabel = 'Dịch'; }
    else if (a === 'running') { displayStatus = 'running'; phaseLabel = 'Kiểm định'; }
    else if (m === 'error' || t === 'error' || a === 'error') displayStatus = 'failed';
    else if (a === 'done' && t === 'done') displayStatus = 'completed';
    else return null; // pending — chưa start
    typeTag = phaseLabel ?? 'Huấn luyện';
  } else {
    const t = file.translationStatus;
    const a = file.auditStatus;
    if (!t) return null;
    // Translate mode 2-phase: translation → optional QC. QC has its own JobManager job
    // (type='quality-check'), so surface running phase if translate done + audit running.
    if (t === 'running') { displayStatus = 'running'; phaseLabel = 'Dịch'; }
    else if (a === 'running') { displayStatus = 'running'; phaseLabel = 'Kiểm tra'; }
    else if (t === 'error') displayStatus = 'failed';
    else if (a === 'error' && t === 'done') displayStatus = 'failed';
    else if (t === 'done') displayStatus = 'completed';
    else return null;

    if (t === 'running') typeTag = 'Dịch';
    else if (a === 'running') typeTag = 'Kiểm tra';
    else if (t === 'done' && a === 'done') typeTag = 'Dịch + Kiểm tra';
    else if (a === 'error' && t === 'done') typeTag = 'Kiểm tra';
    else typeTag = 'Dịch';
  }

  let progress = 0;
  let batch: { current: number; total: number } | undefined;
  let indeterminate = false;

  if (mode === 'translate' && file.batchInfo && file.batchInfo.length > 0) {
    const total = file.batchInfo.length;
    const done = file.batchInfo.filter(b => b.status === 'done').length;

    // Re-translate edge: workflow set translationStatus='running' nhưng KHÔNG reset batchInfo
    // → batchInfo cũ còn all done → tính 100% sai context. Detect stale, hiện indeterminate.
    if (displayStatus === 'running' && done === total) {
      progress = 0;
      indeterminate = true;
    } else {
      progress = Math.round((done / total) * 100);
      batch = { current: done, total };
    }
  } else if (displayStatus === 'running') {
    // Chưa có batchInfo (vừa start) → indeterminate
    indeterminate = true;
  } else {
    progress = STEP_PROGRESS_FALLBACK[file.currentStep] ?? 0;
  }

  if (displayStatus === 'completed') { progress = 100; indeterminate = false; }
  if (displayStatus === 'failed') { progress = 0; indeterminate = false; }

  // Time source priority:
  //   - file.completedAt (set by workflow at status='done'/'error') — persisted disk
  //   - JobManager startTime/endTime — cross-session memory only
  // KHÔNG fallback file.createdAt cho running (gây "2 giờ trước" cho file mới re-translate
  // nhưng JobManager chưa fresh do CORS/network fail → upload time là sai context).
  const fileCompletedAt = toDate((file as any).completedAt);

  const startTime = managerJob ? toDate(managerJob.startTime) : undefined;
  const endTime = displayStatus === 'completed' || displayStatus === 'failed'
    ? (fileCompletedAt ?? (managerJob ? toDate(managerJob.endTime) : undefined))
    : undefined;

  return {
    id: file.id,
    fileName: file.fileName,
    projectName,
    status: displayStatus,
    progress,
    indeterminate,
    batch,
    startTime,
    endTime,
    errorMsg: file.error,
    phaseLabel,
    typeTag,
  };
};

const StatusIcon: React.FC<{ status: DisplayStatus }> = ({ status }) => {
  if (status === 'running') return <Loader2 className="w-3.5 h-3.5 text-amber-600 animate-spin" />;
  if (status === 'completed') return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
  return <AlertCircle className="w-3.5 h-3.5 text-red-600" />;
};

const formatTimeCell = (job: DisplayJob): { primary: string; secondary?: string } => {
  if (job.status === 'running') {
    return { primary: job.startTime ? formatRelative(job.startTime) : 'Đang chạy' };
  }
  if (job.status === 'completed') {
    return { primary: job.endTime ? formatRelative(job.endTime) : 'Đã xong' };
  }
  if (job.status === 'failed') {
    return { primary: job.endTime ? formatRelative(job.endTime) : 'Lỗi' };
  }
  return { primary: '—' };
};

const TableRow: React.FC<{ job: DisplayJob }> = ({ job }) => {
  const isActive = job.status === 'running';
  const cfg = STATUS_CONFIG[job.status];
  const time = formatTimeCell(job);

  const durationText = (() => {
    if (!job.startTime || !job.endTime) return null;
    const diffMs = job.endTime.getTime() - job.startTime.getTime();
    if (diffMs < 0 || diffMs > 6 * 60 * 60 * 1000) return null;
    return formatDuration(job.startTime, job.endTime);
  })();

  // Subtitle below project name — running shows batch progress, failed shows errorMsg.
  // Phase is now shown as typeTag chip so no need to repeat it here.
  const runningSubtitle = isActive
    ? (job.batch ? `${job.batch.current}/${job.batch.total} batch` : (job.indeterminate ? null : `${job.progress}%`))
    : null;
  const errorSubtitle = job.status === 'failed' && job.errorMsg ? job.errorMsg : null;

  return (
    <tr className={`border-b border-slate-100 ${isActive ? 'bg-amber-50/40' : 'hover:bg-slate-50/60'} transition-colors`}>
      {/* File + project + type chip + optional running/error subtitle */}
      <td className="px-3 py-2.5 align-top max-w-0">
        <div className="text-sm font-semibold text-slate-800 truncate" title={job.fileName}>
          {job.fileName}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span className="text-[11px] text-slate-400 truncate" title={job.projectName}>
            {job.projectName}
          </span>
          {(() => {
            const cfg = TYPE_TAG_CONFIG[job.typeTag] ?? { bg: 'bg-slate-100', cls: 'text-slate-600' };
            return (
              <span className={`inline-flex shrink-0 items-center px-1.5 py-0 rounded text-[10px] font-bold ${cfg.bg} ${cfg.cls}`}>
                {job.typeTag}
              </span>
            );
          })()}
        </div>
        {runningSubtitle && (
          <div className="text-[11px] text-amber-700 font-semibold truncate mt-0.5">
            {runningSubtitle}
          </div>
        )}
        {errorSubtitle && (
          <div className="text-[11px] text-red-500 truncate mt-0.5" title={errorSubtitle}>
            {errorSubtitle}
          </div>
        )}
      </td>

      {/* Status — uniform width across all states for visual alignment. */}
      <td className="px-2 py-2.5 align-top whitespace-nowrap">
        <span
          className={`inline-flex items-center justify-center gap-1 w-[88px] px-1.5 py-0.5 ${cfg.bg} ${cfg.cls} text-[10px] font-bold rounded`}
        >
          <StatusIcon status={job.status} />
          {cfg.label}
        </span>
      </td>

      {/* Time */}
      <td className="px-3 py-2.5 align-top text-right whitespace-nowrap">
        <div className="text-[11px] text-slate-500 tabular-nums">{time.primary}</div>
        {durationText && (
          <div className="text-[10px] text-slate-300 tabular-nums">{durationText}</div>
        )}
      </td>
    </tr>
  );
};

const JobsDrawer: React.FC<JobsDrawerProps> = ({ isOpen, onClose, mode, files, projects }) => {
  const { jobs: managerJobs } = useJobManager();
  const [page, setPage] = React.useState(0);

  const projectMap = React.useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach(p => map.set(p.id, p.name || 'Dự án chưa đặt tên'));
    return map;
  }, [projects]);

  const managerJobByFileId = React.useMemo(() => {
    const map = new Map<string, ManagerJob>();
    const expectedTypes = ['translation', 'audit', 'semantic-match', 'quality-check'];
    managerJobs.forEach(j => {
      if (!expectedTypes.includes(j.type)) return;
      const existing = map.get(j.fileId);
      if (!existing || j.startTime > existing.startTime) {
        map.set(j.fileId, j);
      }
    });
    return map;
  }, [managerJobs, mode]);

  const allJobs = React.useMemo(() => {
    return files
      .map(f => mapFileToJob(
        f,
        projectMap.get(f.projectId) || '—',
        mode,
        managerJobByFileId.get(f.id),
      ))
      .filter((j): j is DisplayJob => j !== null);
  }, [files, projectMap, mode, managerJobByFileId]);

  // Single sort by most recent activity time DESC. Running jobs use startTime;
  // completed/failed use endTime. Falls back to 0 for safety.
  const sortByActivityDesc = (a: DisplayJob, b: DisplayJob) => {
    const ta = a.endTime?.getTime() ?? a.startTime?.getTime() ?? 0;
    const tb = b.endTime?.getTime() ?? b.startTime?.getTime() ?? 0;
    return tb - ta;
  };

  const sortedJobs = [...allJobs].sort(sortByActivityDesc);
  // Header counts (still useful for at-a-glance summary).
  const runningCount = sortedJobs.filter(j => j.status === 'running').length;
  const failedCount = sortedJobs.filter(j => j.status === 'failed').length;
  const completedCount = sortedJobs.filter(j => j.status === 'completed').length;

  // Paginate full sorted list (was: only completed).
  const totalPages = Math.max(1, Math.ceil(sortedJobs.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const sortedJobsPaged = sortedJobs.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Reset page when mode changes
  React.useEffect(() => { setPage(0); }, [mode]);

  if (!isOpen) return null;

  const titleMap = {
    translate: { title: 'Công việc dịch', subtitle: 'File đang dịch và file gần đây' },
    train: { title: 'Công việc huấn luyện', subtitle: 'Audit terminology trên các file' },
  }[mode];

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="relative px-5 py-4 border-b border-slate-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50/40 overflow-hidden shrink-0">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-200/30 rounded-full blur-3xl pointer-events-none" />

          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-900 tracking-tight">{titleMap.title}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{titleMap.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {sortedJobs.length > 0 && (
            <div className="relative flex items-center gap-2 mt-3 flex-wrap">
              {runningCount > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-md">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  {runningCount} đang dịch
                </span>
              )}
              {failedCount > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-md">
                  {failedCount} lỗi
                </span>
              )}
              {completedCount > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-md">
                  {completedCount} hoàn thành
                </span>
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {allJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7 text-emerald-300" />
              </div>
              <p className="text-slate-600 font-semibold">Chưa có công việc nào</p>
              <p className="text-sm text-slate-400 mt-1">
                {mode === 'translate'
                  ? 'Upload file vào dự án để bắt đầu dịch.'
                  : 'Nạp tài liệu cặp song ngữ để chạy audit.'}
              </p>
            </div>
          ) : (
            <table className="w-full table-fixed border-collapse">
              <thead className="sticky top-0 z-[2] bg-white">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-3 py-2 text-[10px] font-black tracking-widest uppercase text-slate-500 w-auto">
                    Tên file / Dự án
                  </th>
                  <th className="text-left px-2 py-2 text-[10px] font-black tracking-widest uppercase text-slate-500 w-[88px]">
                    Trạng thái
                  </th>
                  <th className="text-right px-3 py-2 text-[10px] font-black tracking-widest uppercase text-slate-500 w-[110px]">
                    Thời gian
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedJobsPaged.map(j => <TableRow key={j.id} job={j} />)}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer (when total jobs > PAGE_SIZE) */}
        {sortedJobs.length > PAGE_SIZE && (
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between shrink-0">
            <button
              onClick={() => setPage((p: number) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-emerald-700 hover:bg-white rounded-md disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Trước
            </button>
            <span className="text-[11px] text-slate-400 tabular-nums">
              {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, sortedJobs.length)} / {sortedJobs.length}
            </span>
            <button
              onClick={() => setPage((p: number) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-emerald-700 hover:bg-white rounded-md disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-colors"
            >
              Sau
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {sortedJobs.length > 0 && sortedJobs.length <= PAGE_SIZE && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 text-[11px] text-slate-400 text-center shrink-0">
            Tự cập nhật khi job thay đổi trạng thái
          </div>
        )}
      </div>
    </>
  );
};

export default JobsDrawer;
